/**
 * Fabrication Service
 * Handles asset fabrication via Raindrop SmartBuckets/SmartSQL + Vultr compute
 */

import type { Asset, Job, JobStats, IntentProfile, FabricationPlan, FabricationResult } from '../types';
import { FABRICATION_PROFILES } from '../types';
import { useFabStore } from '../store';

// Raindrop SmartBuckets paths
const BUCKET_PATHS = {
  raw: (assetId: string) => `raw/${assetId}/input`,
  plans: (jobId: string) => `plans/${jobId}/plan.json`,
  outputs: (assetId: string, jobId: string) => `outputs/${assetId}/${jobId}`,
};

/**
 * Generate a deterministic fabrication plan from profile + asset stats
 * Uses SmartInference for plan generation (no free-text prompting)
 */
function generatePlan(profile: IntentProfile, _assetStats?: { triangles?: number }): FabricationPlan {
  const config = FABRICATION_PROFILES[profile];
  
  return {
    profile,
    target_triangles: config.target_triangles,
    texture_resolution: config.texture_resolution,
    generate_normals: config.generate_normals,
    generate_ao: config.generate_ao,
    uv_method: profile === 'showcase' ? 'smart_uv' : 'lightmap_pack',
  };
}

/**
 * Upload asset to Raindrop SmartBuckets
 */
async function uploadToSmartBuckets(asset: Asset): Promise<string> {
  // In production: upload to Raindrop SmartBuckets
  // For now: return the existing URL (mock)
  console.log('[Raindrop] Uploading to SmartBuckets:', BUCKET_PATHS.raw(asset.asset_id));
  return asset.raw_url;
}

/**
 * Create job record in SmartSQL
 */
async function createJobRecord(asset: Asset, profile: IntentProfile): Promise<Job> {
  const job: Job = {
    job_id: crypto.randomUUID(),
    asset_id: asset.asset_id,
    profile,
    status: 'pending',
    created_at: new Date().toISOString(),
  };

  // In production: INSERT INTO jobs via SmartSQL
  console.log('[Raindrop] Creating job in SmartSQL:', job.job_id);
  
  return job;
}

/**
 * Store plan in SmartBuckets
 */
async function storePlan(jobId: string, _plan: FabricationPlan): Promise<void> {
  console.log('[Raindrop] Storing plan:', BUCKET_PATHS.plans(jobId));
  // In production: write to SmartBuckets
}

/**
 * Submit job to Vultr conversion worker
 */
async function submitToVultr(
  job: Job,
  rawUrl: string,
  _plan: FabricationPlan
): Promise<{ vultr_job_id: string }> {
  console.log('[Vultr] Submitting conversion job:', {
    job_id: job.job_id,
    raw_download_url: rawUrl,
    callback_url: '/api/vultr/callback',
  });

  // Mock Vultr response - in production this calls the Vultr worker endpoint
  // POST /convert with job_id, raw_download_url, plan.json, callback_url
  return {
    vultr_job_id: `vultr_${crypto.randomUUID().slice(0, 8)}`,
  };
}

/**
 * Poll for job completion (mock implementation)
 */
async function pollJobStatus(job: Job): Promise<Job> {
  // Simulate processing time
  await new Promise((resolve) => setTimeout(resolve, 2000));

  return {
    ...job,
    status: 'completed',
    started_at: new Date(Date.now() - 2000).toISOString(),
    completed_at: new Date().toISOString(),
  };
}

/**
 * Get job stats from SmartSQL
 */
async function getJobStats(job: Job): Promise<JobStats> {
  // Mock stats - in production: SELECT from job_stats via SmartSQL
  const profile = FABRICATION_PROFILES[job.profile];
  
  return {
    job_id: job.job_id,
    input_tris: Math.floor(Math.random() * 500000) + 100000, // 100k-600k
    output_tris: profile.target_triangles,
    input_bytes: Math.floor(Math.random() * 50000000) + 10000000, // 10-60MB
    output_bytes: Math.floor(profile.target_triangles * 50), // ~50 bytes per tri
    texture_res: profile.texture_resolution,
    runtime_ms: Math.floor(Math.random() * 5000) + 2000, // 2-7 seconds
  };
}

/**
 * Get fabricated output URL from SmartBuckets
 */
function getOutputUrl(_assetId: string, _jobId: string): string {
  // In production: return SmartBuckets URL
  // For demo: return a sample optimized GLB
  return `https://s3.amazonaws.com/worldmatica/geomarker_animated.glb`;
}

/**
 * Main fabrication function
 * Orchestrates the full pipeline: upload → plan → Vultr → results
 */
export async function fabricateAsset(
  asset: Asset,
  profile: IntentProfile
): Promise<Job> {
  const store = useFabStore.getState();

  // 1. Upload to SmartBuckets
  const rawUrl = await uploadToSmartBuckets(asset);

  // 2. Create job record in SmartSQL
  const job = await createJobRecord(asset, profile);
  store.setCurrentJob(job);
  store.addJobToHistory(job);

  // 3. Generate fabrication plan via SmartInference
  const plan = generatePlan(profile);

  // 4. Store plan in SmartBuckets
  await storePlan(job.job_id, plan);

  // 5. Submit to Vultr worker
  const { vultr_job_id } = await submitToVultr(job, rawUrl, plan);
  const updatedJob = { ...job, vultr_job_id, status: 'processing' as const };
  store.setCurrentJob(updatedJob);

  // 6. Poll for completion (in production: webhook callback)
  const completedJob = await pollJobStatus(updatedJob);
  store.setCurrentJob(completedJob);

  // 7. Get stats from SmartSQL
  const stats = await getJobStats(completedJob);
  store.setCurrentStats(stats);

  // 8. Get output URL and update fabricated view
  const outputUrl = getOutputUrl(asset.asset_id, completedJob.job_id);
  store.setFabricatedModelUrl(outputUrl);
  store.setViewMode('split');

  // 9. Store result
  const result: FabricationResult = {
    job: completedJob,
    stats,
    output_url: outputUrl,
    preview_url: outputUrl, // Same for now
  };
  store.setFabricationResult(result);

  return completedJob;
}

/**
 * Get fabrication profiles (for SmartMemory display)
 */
export function getProfiles() {
  return FABRICATION_PROFILES;
}
